import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { fmt$0 } from '../utils/formatters';
import { getRange, fmtDateShort } from '../utils/dateRange';
import { parseLocalDate } from '../utils/dates';
import DateRangePicker from './DateRangePicker';

const RANGE_PRESETS=[{id:'week',label:'Week'},{id:'this-month',label:'This Month'},{id:'last-month',label:'Last Month'},{id:'last-3-months',label:'Last 3 Months'},{id:'ytd',label:'Year to Date'},{id:'all',label:'All Time'},{id:'custom',label:'Custom'}];

async function batchIn(table,col,ids,select,extraFilters){
  const all=[];
  for(let i=0;i<ids.length;i+=200){
    const batch=ids.slice(i,i+200);
    let q=supabase.from(table).select(select).in(col,batch);
    if(extraFilters)q=extraFilters(q);
    const{data}=await q;
    if(data)all.push(...data);
  }
  return all;
}

export default function VendorsPage({orgId,t,si,sb,sh,sd,mn,settings,setInvoiceModalId,salesDate,setSalesDate}){
const[vendors,setVendors]=useState([]);
const[selectedVendor,setSelectedVendor]=useState(null);
const[aliases,setAliases]=useState([]);
const[vSearch,setVSearch]=useState('');
const[dropOpen,setDropOpen]=useState(false);

const[invoices,setInvoices]=useState([]);
const[purchases,setPurchases]=useState([]);
const[expenses,setExpenses]=useState([]);
const[dataLoading,setDataLoading]=useState(false);

const[viewMode,setViewMode]=useState('ytd');
const[weekOffset,setWeekOffset]=useState(0);
const[customStart,setCustomStart]=useState('');
const[customEnd,setCustomEnd]=useState('');

// Load vendor list
useEffect(()=>{
  if(!orgId)return;
  (async()=>{
    const{data}=await supabase.from('vendors').select('id,name,vendor_category,is_active').eq('org_id',orgId).eq('is_active',true).order('name');
    setVendors(data||[]);
  })();
},[orgId]);

// On vendor select: fetch aliases, then all org data and match in code
const loadVendorData=useCallback(async(vendor)=>{
  if(!vendor||!orgId)return;
  setDataLoading(true);
  setInvoices([]);setPurchases([]);setExpenses([]);

  // 1. Fetch aliases
  const{data:alRows}=await supabase.from('vendor_aliases').select('alias').eq('vendor_id',vendor.id);
  const aliasList=(alRows||[]).map(a=>a.alias);
  setAliases(aliasList);

  // 2. Build match set (lowercase trimmed)
  const nameSet=new Set([vendor.name,...aliasList].map(n=>n.toLowerCase().trim()));

  // 3. Fetch all org invoices, purchases, expenses (only needed columns)
  const[{data:allInv},{data:allPu},{data:allCe}]=await Promise.all([
    supabase.from('invoices').select('id,vendor,invoice_number,invoice_date,grand_total,total').eq('org_id',orgId).is('superseded_by',null).order('invoice_date',{ascending:false}).limit(20000),
    supabase.from('purchases').select('id,vendor,amount,date,category,invoice_id').eq('org_id',orgId).eq('is_superseded',false).order('date',{ascending:false}).limit(20000),
    supabase.from('categorized_expenses').select('id,vendor,description,amount,date,category,source_invoice_id').eq('org_id',orgId).eq('is_superseded',false).order('date',{ascending:false}).limit(20000),
  ]);

  // 4. Match invoices by vendor name in code
  const matchedInvs=(allInv||[]).filter(inv=>nameSet.has((inv.vendor||'').toLowerCase().trim()));
  const matchedInvIds=new Set(matchedInvs.map(inv=>inv.id));

  // 5. For matched invoices, find purchases/expenses by invoice_id/source_invoice_id
  const invPurchases=(allPu||[]).filter(p=>p.invoice_id&&matchedInvIds.has(p.invoice_id));
  const invExpenses=(allCe||[]).filter(e=>e.source_invoice_id&&matchedInvIds.has(e.source_invoice_id));

  // 6. Manual entries: match by vendor name, no invoice link
  const manualPurchases=(allPu||[]).filter(p=>!p.invoice_id&&nameSet.has((p.vendor||'').toLowerCase().trim()));
  const manualExpenses=(allCe||[]).filter(e=>!e.source_invoice_id&&nameSet.has((e.vendor||'').toLowerCase().trim()));

  // 7. Build invoice rows with COGS/expense breakdown
  const puByInv=new Map();
  for(const p of invPurchases){if(!puByInv.has(p.invoice_id))puByInv.set(p.invoice_id,[]);puByInv.get(p.invoice_id).push(p);}
  const ceByInv=new Map();
  for(const e of invExpenses){if(!ceByInv.has(e.source_invoice_id))ceByInv.set(e.source_invoice_id,[]);ceByInv.get(e.source_invoice_id).push(e);}

  const invRows=matchedInvs.map(inv=>{
    const gt=Number(inv.grand_total||inv.total||0);
    const cogsRows=puByInv.get(inv.id)||[];
    const expRows=ceByInv.get(inv.id)||[];
    const cogs=cogsRows.reduce((s,p)=>s+Number(p.amount||0),0);
    const exp=expRows.reduce((s,e)=>s+Number(e.amount||0),0);
    return{...inv,grandTotal:gt,cogs,exp,diff:Math.round((gt-cogs-exp)*100)/100};
  });

  setInvoices(invRows);
  setPurchases(manualPurchases);
  setExpenses(manualExpenses);
  setDataLoading(false);
},[orgId]);

function selectVendor(v){
  setSelectedVendor(v);setVSearch('');setDropOpen(false);
  loadVendorData(v);
}

// Date range
let rangeStart,rangeEnd,rangeLabel;
if(viewMode==='week'){
  const ws=(settings?.weekStart||0);
  const d=parseLocalDate(salesDate);const dow=d.getDay();const diff=(dow-ws+7)%7;
  const s=new Date(d);s.setDate(d.getDate()-diff+weekOffset*7);
  const e=new Date(s);e.setDate(s.getDate()+6);
  rangeStart=s.toISOString().split('T')[0];rangeEnd=e.toISOString().split('T')[0];
  const fD=d2=>parseLocalDate(d2).toLocaleDateString('en-US',{month:'short',day:'numeric'});
  rangeLabel=`Week of ${fD(rangeStart)} – ${fD(rangeEnd)}`;
}else if(viewMode==='all'){
  rangeStart='2000-01-01';rangeEnd='2099-12-31';rangeLabel='All Time';
}else{
  const r=getRange(viewMode,{start:customStart,end:customEnd});
  rangeStart=r.start;rangeEnd=r.end;rangeLabel=viewMode==='custom'?`${fmtDateShort(r.start)} – ${fmtDateShort(r.end)}`:r.label;
}

const filteredInvs=invoices.filter(inv=>{const d=inv.invoice_date;return d>=rangeStart&&d<=rangeEnd;});
const filteredPu=purchases.filter(p=>p.date>=rangeStart&&p.date<=rangeEnd);
const filteredCe=expenses.filter(e=>e.date>=rangeStart&&e.date<=rangeEnd);

const cogsTotal=filteredInvs.reduce((s,inv)=>s+inv.cogs,0)+filteredPu.reduce((s,p)=>s+Number(p.amount||0),0);
const expTotal=filteredInvs.reduce((s,inv)=>s+inv.exp,0)+filteredCe.reduce((s,e)=>s+Number(e.amount||0),0);
const totalSpent=cogsTotal+expTotal;

const filteredVendors=vSearch?vendors.filter(v=>v.name.toLowerCase().includes(vSearch.toLowerCase())):vendors;

const fA=v=>'$'+Math.abs(v).toLocaleString(undefined,{minimumFractionDigits:2});

return(<div>
<div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
<h1 style={{fontSize:22,fontWeight:700,margin:0}}>Vendor Reports</h1>
</div>

{/* Vendor picker */}
<div style={{position:'relative',marginBottom:16,maxWidth:400}}>
<input value={dropOpen?vSearch:(selectedVendor?.name||'')} onChange={e=>{setVSearch(e.target.value);setDropOpen(true);}} onFocus={()=>{setDropOpen(true);setVSearch('');}} placeholder="Search vendors..." style={{...si,width:'100%',fontSize:13,padding:'10px 12px'}} onBlur={()=>setTimeout(()=>setDropOpen(false),200)}/>
{dropOpen&&<div style={{position:'absolute',top:'100%',left:0,right:0,background:t.cd,border:`1px solid ${t.bd}`,borderRadius:8,maxHeight:300,overflow:'auto',zIndex:20,marginTop:2,boxShadow:'0 4px 12px rgba(0,0,0,0.1)'}}>
{filteredVendors.length===0?<div style={{padding:12,color:t.td,fontSize:12}}>No vendors found</div>:
filteredVendors.map(v=><div key={v.id} onMouseDown={()=>selectVendor(v)} style={{padding:'10px 14px',cursor:'pointer',borderBottom:`1px solid ${t.bd}`,fontSize:13,display:'flex',justifyContent:'space-between',alignItems:'center'}} onMouseOver={e=>e.currentTarget.style.background=t.sfh} onMouseOut={e=>e.currentTarget.style.background='transparent'}>
<span style={{fontWeight:500}}>{v.name}</span>
<span style={{fontSize:9,padding:'2px 6px',borderRadius:3,background:v.vendor_category==='expense'?'rgba(212,168,36,0.1)':'rgba(59,130,246,0.1)',color:v.vendor_category==='expense'?t.yl:t.bl}}>{v.vendor_category||'inventory'}</span>
</div>)}
</div>}
</div>

{!selectedVendor?<div style={{textAlign:'center',padding:60,color:t.td,fontSize:14}}>Select a vendor to view their history</div>:dataLoading?<div style={{textAlign:'center',padding:60,color:t.td,fontSize:14}}>Loading...</div>:<>

{/* Aliases */}
{aliases.length>0&&<div style={{marginBottom:12,display:'flex',gap:6,flexWrap:'wrap',alignItems:'center'}}>
<span style={{fontSize:10,color:t.td}}>Also known as:</span>
{aliases.map(a=><span key={a} style={{fontSize:11,padding:'2px 8px',borderRadius:4,background:t.sf,border:`1px solid ${t.bd}`}}>{a}</span>)}
</div>}

{/* Date filters */}
<div style={{background:t.cd,borderRadius:12,border:`1px solid ${t.bd}`,padding:14,marginBottom:12}}>
<div style={{display:'flex',gap:6,marginBottom:8,flexWrap:'wrap'}}>
{RANGE_PRESETS.map(p=><button key={p.id} onClick={()=>{setViewMode(p.id);setWeekOffset(0);}} style={{...sb,background:viewMode===p.id?t.bl:t.sf,color:viewMode===p.id?'#fff':t.tm,border:`1px solid ${viewMode===p.id?t.bl:t.bd}`,padding:'5px 12px',fontSize:10}}>{p.label}</button>)}
</div>
{viewMode==='custom'&&<DateRangePicker start={customStart} end={customEnd} onChange={(s,e)=>{setCustomStart(s);setCustomEnd(e);}} t={t} si={si}/>}
{viewMode==='week'&&<div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8}}>
<button onClick={()=>setWeekOffset(p=>p-1)} style={{...sb,background:t.sf,color:t.tm,border:`1px solid ${t.bd}`,padding:'4px 10px',fontSize:12}}>←</button>
<div style={{fontSize:12,color:t.tm,flex:1,textAlign:'center',fontWeight:600}}>{rangeLabel}</div>
<button onClick={()=>setWeekOffset(p=>p+1)} style={{...sb,background:t.sf,color:t.tm,border:`1px solid ${t.bd}`,padding:'4px 10px',fontSize:12}}>→</button>
{weekOffset!==0&&<button onClick={()=>setWeekOffset(0)} style={{...sb,background:t.sf,color:t.bl,border:`1px solid ${t.bd}`,padding:'4px 8px',fontSize:10}}>Today</button>}
</div>}
{viewMode!=='week'&&<div style={{fontSize:12,color:t.tm,fontWeight:600}}>{rangeLabel}</div>}
</div>

{/* Summary */}
<div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:16}}>
{[['Total Spent',totalSpent,t.tm],['COGS',cogsTotal,t.gn],['Other Expenses',expTotal,t.yl],['Invoices',filteredInvs.length,t.bl]].map(([label,val,color])=>
<div key={label} style={{background:t.cd,border:`1px solid ${t.bd}`,borderRadius:8,padding:'12px 14px'}}>
<div style={{fontSize:9,color:t.td,textTransform:'uppercase',marginBottom:4}}>{label}</div>
<div style={{fontSize:18,fontWeight:700,fontFamily:mn,color}}>{typeof val==='number'&&label!=='Invoices'?fA(val):val}</div>
</div>)}
</div>

{/* Invoice table */}
{filteredInvs.length>0&&<div style={{marginBottom:20}}>
<div style={{fontSize:14,fontWeight:600,marginBottom:8}}>Invoices</div>
<div style={{background:t.cd,borderRadius:12,border:`1px solid ${t.bd}`,overflow:'hidden'}}>
<table style={{width:'100%',borderCollapse:'collapse'}}>
<thead><tr style={{background:t.sf}}>{['Date','Invoice #','Grand Total','COGS','Expenses','Difference'].map(h=><th key={h} style={{...sh,fontSize:9}}>{h}</th>)}</tr></thead>
<tbody>{filteredInvs.map(inv=>{
const hasDiff=Math.abs(inv.diff)>=0.01;
return <tr key={inv.id} style={{borderBottom:`1px solid ${t.bd}`,cursor:'pointer'}} onClick={()=>setInvoiceModalId(inv.id)} onMouseOver={e=>e.currentTarget.style.background=t.sfh} onMouseOut={e=>e.currentTarget.style.background='transparent'}>
<td style={{...sd,fontSize:12}}>{inv.invoice_date?parseLocalDate(inv.invoice_date).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'2-digit'}):'—'}</td>
<td style={{...sd,fontSize:11,fontWeight:500}}>{inv.invoice_number||'—'}</td>
<td style={{...sd,fontFamily:mn,fontSize:11,fontWeight:600}}>{fA(inv.grandTotal)}</td>
<td style={{...sd,fontFamily:mn,fontSize:11,color:t.gn}}>{fA(inv.cogs)}</td>
<td style={{...sd,fontFamily:mn,fontSize:11,color:t.yl}}>{fA(inv.exp)}</td>
<td style={{...sd,fontFamily:mn,fontSize:11,fontWeight:hasDiff?600:400,color:hasDiff?t.ac:t.td}}>{hasDiff?fA(inv.diff):'—'}</td>
</tr>;})}
{filteredInvs.length>1&&<tr style={{background:t.sf}}>
<td style={{...sd,fontWeight:700}} colSpan={2}>Total</td>
<td style={{...sd,fontFamily:mn,fontWeight:700}}>{fA(filteredInvs.reduce((s,i)=>s+i.grandTotal,0))}</td>
<td style={{...sd,fontFamily:mn,fontWeight:700,color:t.gn}}>{fA(filteredInvs.reduce((s,i)=>s+i.cogs,0))}</td>
<td style={{...sd,fontFamily:mn,fontWeight:700,color:t.yl}}>{fA(filteredInvs.reduce((s,i)=>s+i.exp,0))}</td>
<td style={{...sd,fontFamily:mn,fontWeight:700,color:t.ac}}>{fA(filteredInvs.reduce((s,i)=>s+i.diff,0))}</td>
</tr>}
</tbody>
</table>
</div>
</div>}

{/* Manual purchases */}
{filteredPu.length>0&&<div style={{marginBottom:20}}>
<div style={{fontSize:14,fontWeight:600,marginBottom:8}}>Manual Purchases (no invoice)</div>
<div style={{background:t.cd,borderRadius:12,border:`1px solid ${t.bd}`,overflow:'hidden'}}>
<table style={{width:'100%',borderCollapse:'collapse'}}>
<thead><tr style={{background:t.sf}}>{['Date','Category','Amount'].map(h=><th key={h} style={{...sh,fontSize:9}}>{h}</th>)}</tr></thead>
<tbody>{filteredPu.map(p=><tr key={p.id} style={{borderBottom:`1px solid ${t.bd}`}}>
<td style={{...sd,fontSize:12}}>{parseLocalDate(p.date).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'2-digit'})}</td>
<td style={sd}><span style={{fontSize:9,padding:'2px 5px',borderRadius:3,background:'rgba(59,130,246,0.1)',color:t.bl}}>{p.category||'Food'}</span></td>
<td style={{...sd,fontFamily:mn,fontSize:11,fontWeight:600}}>{fA(Number(p.amount||0))}</td>
</tr>)}
{filteredPu.length>1&&<tr style={{background:t.sf}}>
<td style={{...sd,fontWeight:700}} colSpan={2}>Total</td>
<td style={{...sd,fontFamily:mn,fontWeight:700}}>{fA(filteredPu.reduce((s,p)=>s+Number(p.amount||0),0))}</td>
</tr>}
</tbody>
</table>
</div>
</div>}

{/* Manual expenses */}
{filteredCe.length>0&&<div style={{marginBottom:20}}>
<div style={{fontSize:14,fontWeight:600,marginBottom:8}}>Manual Expenses (no invoice)</div>
<div style={{background:t.cd,borderRadius:12,border:`1px solid ${t.bd}`,overflow:'hidden'}}>
<table style={{width:'100%',borderCollapse:'collapse'}}>
<thead><tr style={{background:t.sf}}>{['Date','Description','Category','Amount'].map(h=><th key={h} style={{...sh,fontSize:9}}>{h}</th>)}</tr></thead>
<tbody>{filteredCe.map(e=><tr key={e.id} style={{borderBottom:`1px solid ${t.bd}`}}>
<td style={{...sd,fontSize:12}}>{parseLocalDate(e.date).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'2-digit'})}</td>
<td style={{...sd,fontSize:11}}>{e.description||'—'}</td>
<td style={sd}><span style={{fontSize:9,padding:'2px 5px',borderRadius:3,background:'rgba(212,168,36,0.1)',color:t.yl}}>{e.category||'Miscellaneous'}</span></td>
<td style={{...sd,fontFamily:mn,fontSize:11,fontWeight:600}}>{fA(Number(e.amount||0))}</td>
</tr>)}
{filteredCe.length>1&&<tr style={{background:t.sf}}>
<td style={{...sd,fontWeight:700}} colSpan={3}>Total</td>
<td style={{...sd,fontFamily:mn,fontWeight:700}}>{fA(filteredCe.reduce((s,e)=>s+Number(e.amount||0),0))}</td>
</tr>}
</tbody>
</table>
</div>
</div>}

{filteredInvs.length===0&&filteredPu.length===0&&filteredCe.length===0&&<div style={{textAlign:'center',padding:40,color:t.td,fontSize:12}}>No activity for {selectedVendor.name} in this period</div>}

</>}
</div>);
}
